import {
  ssoFromCampusSelect,
  useSsoFromCampusOptions,
} from '@/api/generated/v2-campus-link/v2-campus-link'
import type {
  CampusSsoOptionItem,
  CampusSsoOptionsResponse,
  CampusSsoSelectResponse,
} from '@/api/schemas'
import { EntryCard } from '@/components/shop/EntryCard'
import { ShopOptionList } from '@/components/shop/ShopOptionList'
import { shopKey } from '@/components/shop/shopOptions'
import { authApi } from '@/lib/api'
import { takeReturnPath } from '@/lib/returnPath'
import { createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

/**
 * /campus-sso/select?sso_token=<jwt>
 *
 * 캠퍼스에서 "솔루션 ↗" 클릭 후 솔루션 SSO 진입점이 redirect로 보내는 페이지.
 *
 * - 옵션 1개 → 자동으로 select 호출 (UI 스킵, 사용자에게 spinner만)
 * - 옵션 다수 → mall × shop picker 렌더 → 클릭 시 select
 * - 0개는 백엔드에서 /no-linked-mall로 redirect되므로 여기 도달 안 함 (방어 안내만)
 *
 * select 성공 응답의 access_token을 sessionStorage에 저장 후 `/`로 navigate.
 *
 * NOTE: 쿼리 파라미터명 `sso_token`은 의도. __root.tsx가 `?token=`을 OAuth 콜백
 * access_token으로 자동 저장해버리는 충돌 회피용.
 */
export const Route = createFileRoute('/campus-sso/select')({
  component: CampusSsoSelectPage,
  validateSearch: (search) => ({
    sso_token: (search.sso_token as string | undefined) ?? '',
  }),
})

/** 캠퍼스 홈 — 토큰 만료 시 재시도 진입점. 환경변수 미설정 시 dev fallback (no-linked-mall 과 동일). */
const CAMPUS_URL =
  import.meta.env.VITE_CAMPUS_URL || 'https://dev.1bpluschool.com'

function CampusSsoSelectPage() {
  const { sso_token: token } = Route.useSearch()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // 누른 행에만 스피너를 보이기 위한 키
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  // 토큰 만료/무효 감지 시 재시작 안내 화면으로 전환 (raw 에러 노출 방지)
  const [sessionExpired, setSessionExpired] = useState(false)
  // 단일 옵션 자동 진입 race 방지
  const autoSelectFiredRef = useRef(false)

  // 옵션 조회 (멱등 — 새로고침해도 같은 결과)
  const {
    data: optionsRaw,
    isLoading,
    error: optionsError,
  } = useSsoFromCampusOptions(
    { token },
    { query: { enabled: !!token, retry: false } },
  )
  const options = (optionsRaw ?? null) as CampusSsoOptionsResponse | null

  // (mall_id, shop_no) 선택 → access_token 받아 세션 저장 후 dashboard 진입
  const handleSelect = async (item: CampusSsoOptionItem) => {
    if (submitting) return
    setSubmitting(true)
    setPendingKey(shopKey(item))
    setError(null)
    try {
      const response = (await ssoFromCampusSelect({
        token,
        mall_id: item.mall_id,
        shop_no: item.shop_no,
      })) as unknown as CampusSsoSelectResponse
      if (!response?.access_token) {
        throw new Error('access_token이 응답에 없습니다.')
      }
      authApi.setToken(response.access_token)
      // 랜딩에서 적어둔 복귀 경로가 있으면 그리로, 없으면 대시보드 홈으로.
      // (검증·만료·삭제는 takeReturnPath 안에서 끝난다)
      window.location.href = takeReturnPath() ?? '/'
    } catch (e: any) {
      // 토큰 만료/무효/이미 사용(422) 또는 대상 소멸(404)은 같은 토큰으로 복구 불가 —
      // picker 에 raw 에러를 남기지 말고 "만료 → 캠퍼스에서 다시" 안내 화면으로 전환.
      if (e?.status === 422 || e?.status === 404) {
        setSessionExpired(true)
        setSubmitting(false)
        return
      }
      // 그 외(예: Cafe24 재인증 필요 409 CAFE24_REAUTH_REQUIRED)는 사용자용 한국어
      // 메시지라 배너로 노출 —
      // 다른 샵은 정상일 수 있어 picker 에 머문다.
      const message =
        e?.message ||
        '진입에 실패했습니다. 자사몰 사관학교 캠퍼스에서 다시 시도해주세요.'
      setError(message)
      setSubmitting(false)
      setPendingKey(null)
    }
  }

  // 단일 옵션이면 자동 진입 (사용자에게 picker 노출 안 함)
  useEffect(() => {
    if (
      !autoSelectFiredRef.current &&
      options &&
      (options.items ?? []).length === 1 &&
      !error
    ) {
      autoSelectFiredRef.current = true
      handleSelect((options.items ?? [])[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  // 토큰 누락
  if (!token) {
    return <ErrorView message="잘못된 접근입니다. 자사몰 사관학교 캠퍼스에서 다시 시도해주세요." />
  }

  // select 실패로 세션(토큰) 만료 감지 — picker/스피너보다 우선.
  if (sessionExpired) {
    return <SessionExpiredView />
  }

  // 옵션 조회 실패 (만료된 토큰 등) — 진입 시점에 이미 만료. 재시도 화면으로.
  if (optionsError) {
    return <SessionExpiredView />
  }

  // 단일 옵션 자동 진입 중 또는 옵션 로딩 중
  if (isLoading || (options && (options.items ?? []).length === 1 && !error)) {
    return <SpinnerView label="솔루션 대시보드로 이동 중…" />
  }

  // 옵션 0개 (백엔드가 /no-linked-mall로 보냈어야 함 — 방어용)
  if (!options || (options.items ?? []).length === 0) {
    return (
      <ErrorView message="연결된 자사몰이 없어요. 솔루션 가입 후 자사몰 사관학교 캠퍼스 계정과 연결해주세요." />
    )
  }

  // 다수 옵션 picker — 행 모양(몰 묶음 · 이름 · 도메인 · #번호)은 등록 화면·헤더
  // 전환기와 공유한다 (REQ-54). 몰이 여럿이면 몰별로 묶어 보인다.
  const items = options.items ?? []

  return (
    <EntryCard
      title="어느 샵으로 들어갈까요?"
      description={
        options.campus_user_name ? (
          <>
            <span className="font-medium text-on-surface">
              {options.campus_user_name}
            </span>
            님의 자사몰 사관학교 캠퍼스 계정에 연결된 샵이에요.
          </>
        ) : (
          '자사몰 사관학교 캠퍼스 계정에 연결된 샵이에요.'
        )
      }
    >
      {error && (
        <div className="mb-4 rounded-lg bg-error/5 border border-error/20 px-4 py-3 text-sm text-error">
          {error}
        </div>
      )}

      <ShopOptionList
        items={items}
        onSelect={handleSelect}
        disabled={submitting}
        ariaLabel="들어갈 샵"
        trailing={(item) =>
          pendingKey === shopKey(item) ? (
            <Loader2 className="h-4 w-4 text-outline-variant animate-spin" />
          ) : (
            <ExternalLink className="h-4 w-4 text-outline-variant" />
          )
        }
      />
    </EntryCard>
  )
}

function SpinnerView({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface gap-3">
      <Loader2 className="h-8 w-8 text-outline-variant animate-spin" />
      <p className="text-sm text-on-surface-variant">{label}</p>
    </div>
  )
}

/**
 * 토큰 만료/무효 시 안내 — SSO 토큰은 보안상 잠깐만 유효하다. raw JWT 에러 대신
 * 무엇을 해야 하는지(캠퍼스에서 다시 열기)를 명확히 보여준다.
 */
function SessionExpiredView() {
  return (
    <EntryCard
      title="연결 시간이 만료됐어요"
      description="보안을 위해 잠깐 동안만 유효한 연결이에요. 자사몰 사관학교 캠퍼스에서 '솔루션 열기'를 다시 눌러 주세요."
    >
      <a
        href={CAMPUS_URL}
        className="flex items-center justify-center gap-2 w-full min-h-[44px] rounded-lg border border-[#ff5757] bg-surface-lowest text-[#ff5757] py-2.5 text-sm font-medium hover:bg-[#ff5757]/10 transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        자사몰 사관학교 캠퍼스로 돌아가기
      </a>
    </EntryCard>
  )
}

function ErrorView({ message }: { message: string }) {
  return <EntryCard title="솔루션에 들어갈 수 없어요" description={message} />
}
